import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import './Carousel.scss';

export interface CarouselProps {
  children: any;
  options?: EmblaOptionsType;
  autoplay?: boolean;
  autoplayOptions?: AutoplayOptionsType;
  dots?: boolean;
  onChangeSlide?: (slideNo: number) => void;
  scrollToIndex?: number;
  styles?: { containerStyle?: CSSProperties; dotsStyle?: CSSProperties; style?: CSSProperties };
}

export const DotButton: React.FC<any> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button type="button" {...restProps}>
      {children}
    </button>
  );
};

export const Carousel: React.FC<CarouselProps> = (props) => {
  const { children, options, autoplay, dots, scrollToIndex, autoplayOptions } = props;
  const plugins = [];
  if (autoplay) {
    plugins.push(Autoplay(autoplayOptions));
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentHeight, setCurrentHeight] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  const updateHeight = useCallback(() => {
    if (emblaApi && containerRef.current) {
      const currentSlide = emblaApi.slideNodes()[emblaApi.selectedScrollSnap()];
      if (currentSlide) {
        const slideHeight = currentSlide.getBoundingClientRect().height;
        setCurrentHeight(slideHeight);
      }
    }
  }, [emblaApi]);

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      setScrollSnaps(emblaApi.scrollSnapList());
      // 초기 높이 설정
      setTimeout(() => updateHeight(), 0);
    },
    [updateHeight],
  );

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      props.onChangeSlide && props.onChangeSlide(emblaApi.selectedScrollSnap());
      // 슬라이드 변경 시 높이 업데이트
      updateHeight();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateHeight],
  );

  useEffect(() => {
    if (emblaApi) {
      onInit(emblaApi);
      onSelect(emblaApi);
      emblaApi.on('reInit', onInit);
      emblaApi.on('reInit', onSelect);
      emblaApi.on('select', onSelect);
    }

    return () => {
      if (emblaApi) {
        emblaApi.off('reInit', onInit);
        emblaApi.off('reInit', onSelect);
        emblaApi.off('select', onSelect);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (scrollToIndex !== undefined) {
      scrollTo(scrollToIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToIndex]);

  // 윈도우 리사이즈 시 높이 재계산
  useEffect(() => {
    const handleResize = () => {
      updateHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateHeight]);

  //  children: <div className="embla__slide">Slide 1</div>

  return (
    <div
      className="embla"
      ref={emblaRef}
      style={{
        ...props.styles?.style,
        height: currentHeight ? `${currentHeight}px` : 'auto',
        transition: 'height 0.3s ease-in-out',
      }}>
      <div className="embla__container" style={props.styles?.containerStyle} ref={containerRef}>
        {children}
      </div>
      {dots && (
        <div className="embla__dots" style={props.styles?.dotsStyle}>
          {scrollSnaps.map((_, index) => {
            return (
              <DotButton
                key={index}
                onClick={() => scrollTo(index)}
                className={'embla__dot'.concat(index === selectedIndex ? ' embla__dot--selected' : '')}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
